'use client';

import { Button } from '@/components/ui/button';
import { FileInput, FileUploader, FileUploaderContent, FileUploaderItem } from '@/components/ui/file-upload';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { CloudUpload, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';
import { useContactUsMutation } from './use-contact-us-mutation';

export const ContactUsForm = () => {
  const [files, setFiles] = useState<File[] | null>(null);
  const dropZoneConfig = {
    maxFiles: 5,
    maxSize: 1024 * 1024 * 5,
    multiple: true,
  };

  const formSchema = z
    .object({
      topic: z.string(),
      email: z.string().trim().email(),
      name: z.string().min(1, 'Name is required'),
      orderPlatform: z.string().optional(),
      orderNumber: z.string().optional(),
      message: z.string().min(1, 'Please enter your message'),
      file: z.string().optional(),
    })
    .refine(
      data => {
        // Order Platform is required when topic IS product-defects
        if (data.topic === 'product-defects') {
          return !!data.orderPlatform;
        }

        return true;
      },
      {
        message: 'Order platform is required',
        path: ['orderPlatform'],
      },
    )
    .refine(
      data => {
        // Order Number is required when topic IS product-defects
        if (data.topic === 'product-defects') {
          return !!data.orderNumber;
        }

        return true;
      },
      {
        message: 'Order number is required',
        path: ['orderNumber'],
      },
    )
    .refine(
      data => {
        // Order Number is required when topic IS product-defects
        if (data.topic === 'product-defects') {
          return !!data.orderNumber;
        }

        return true;
      },
      {
        message: 'File is required',
        path: ['file'],
      },
    );

  type typeFormSchema = typeof formSchema;

  const form = useForm<z.infer<typeFormSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      topic: 'product-defects',
      email: '',
      name: '',
      orderPlatform: '',
      orderNumber: '',
      message: '',
      file: '',
    },
  });

  // const topic = form.watch("topic")

  const showVariant1 = form.watch('topic') === 'product-defects';
  const showVariant2 = !showVariant1;

  const {
    submitContactForm,
    loading,
    // error,
    // isSuccess,
    responseMessage,
  } = useContactUsMutation();

  async function onSubmit(values: z.infer<typeFormSchema>) {
    try {
      if (form.watch('topic') === 'product-defects' && !files) {
        toast.error('Please upload a file');

        form.setError('file', {
          type: 'manual',
          message: 'File is required',
        });

        return;
      }

      const result = await submitContactForm({
        ...values,
        email: values.email.trim(),
        files: files,
      });

      if (result && result.code === '200') {
        toast.success(responseMessage || 'Form submitted successfully');
        form.reset({
          topic: '',
          email: '',
          name: '',
          orderPlatform: '',
          orderNumber: '',
          message: '',
          file: '',
        });
        setFiles(null);
      } else {
        toast.error(responseMessage || 'Failed to submit the form');
      }
    } catch (error) {
      console.error('Form submission error', error);
      toast.error('Failed to submit the form. Please try again.');
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='basis-1/2 space-y-8'
      >
        <h2 className='text-[16px] font-bold text-title'>Contact Form</h2>

        <FormField
          control={form.control}
          name='topic'
          render={({ field }) => (
            <FormItem>
              <FormLabel
                required
                className='text-title'
              >
                Select a topic
              </FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormControl>
                  <SelectTrigger className='rounded-[10px] text-title'>
                    <SelectValue placeholder='Have issue placing the order?' />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value='product-defects'>Product defects/missing parts</SelectItem>
                  <SelectItem value='have-issue-placing-the-order'>Have issue placing the order</SelectItem>
                  <SelectItem value='shipping-returns'>Shipping & Returns</SelectItem>
                  <SelectItem value='wholesale-inquiry'>Wholesale Inquiry</SelectItem>
                  <SelectItem value='press-inquiry'>Press Inquiry</SelectItem>
                  <SelectItem value='other'>Other</SelectItem>
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel
                required
                className='text-title'
              >
                Email address
              </FormLabel>
              <FormControl>
                <Input
                  placeholder='Enter your email'
                  type='email'
                  className='rounded-[10px]'
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormLabel
                required
                className='text-title'
              >
                Name
              </FormLabel>
              <FormControl>
                <Input
                  placeholder='Enter your name'
                  className='rounded-[10px]'
                  type=''
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        {showVariant1 && (
          <FormField
            control={form.control}
            name='orderPlatform'
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  required
                  className='text-title'
                >
                  Order Platform
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className='rounded-[10px] text-title'>
                      <SelectValue placeholder='Please select order platform' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='kingsyard'>kingsyard.com</SelectItem>
                    <SelectItem value='amazon'>Amazon</SelectItem>
                    <SelectItem value='walmart'>Walmart</SelectItem>
                    <SelectItem value='other'>Other</SelectItem>
                  </SelectContent>
                </Select>

                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {showVariant1 && (
          <FormField
            control={form.control}
            name='orderNumber'
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  required
                  className='text-title'
                >
                  Order Number
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder='Enter your order number'
                    type=''
                    className='rounded-[10px]'
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name='message'
          render={({ field }) => (
            <FormItem>
              <FormLabel
                required
                className='text-title'
              >
                Message
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder={
                    showVariant1
                      ? `Please specify the issues.
For example: missing ant moat for the hummingbird feeder`
                      : 'Leave your message here'
                  }
                  className='resize-none rounded-[10px]'
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='file'
          render={({ field }) => (
            <FormItem>
              <FormLabel
                className='text-title'
                required={form.watch('topic') === 'product-defects'}
              >
                Attachments
              </FormLabel>
              {showVariant1 && (
                <FormDescription className='text-sm text-title'>
                  Please add the photos of the product defects or missing parts so that we can confirm it.
                </FormDescription>
              )}
              <FormControl>
                <FileUploader
                  value={files}
                  onValueChange={setFiles}
                  dropzoneOptions={dropZoneConfig}
                  className='bg-background outline-slate-500 relative outline-dashed outline-1'
                >
                  <FileInput
                    id='fileInput'
                    className=''
                  >
                    <div className='flex w-full flex-col items-center justify-center p-8 '>
                      <CloudUpload className='text-gray-500 h-10 w-10' />
                      <p className='dark_text-gray-400 text-gray-500 mb-1 text-sm'>
                        <span className='font-semibold'>Click to upload</span>
                        &nbsp; or drag and drop
                      </p>
                      <p className='dark_text-gray-400 text-gray-500 text-xs'>SVG, PNG, JPG or GIF</p>
                    </div>
                  </FileInput>
                  <FileUploaderContent>
                    {files &&
                      files.length > 0 &&
                      files.map((file, i) => (
                        <FileUploaderItem
                          key={i}
                          index={i}
                        >
                          <Paperclip className='stroke-current h-4 w-4' />
                          <span>{file.name}</span>
                        </FileUploaderItem>
                      ))}
                  </FileUploaderContent>
                </FileUploader>
              </FormControl>
              <FormDescription className='text-sm text-title'>Add Attachments(up to 5 files, 5mb max.)</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type='submit'
          className='h-[55px] w-full rounded-[10px] font-poppins font-[600] hover_opacity-90'
          disabled={loading}
        >
          Submit
        </Button>
      </form>
    </Form>
  );
};
